import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/http";
import { fmtCurrency } from "@/lib/calc";
import { CheckCircle2, AlertCircle, Landmark } from "lucide-react";

const Stat = ({ l, v, tone }) => <div className="rounded-xl border p-3"><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{l}</div><div className={`font-heading font-bold text-lg tabular-nums ${tone || ""}`}>{v}</div></div>;

// One payout → every Stripe charge/refund inside it, matched to the Azordi sale.
export const PayoutReconcileDialog = ({ payoutId, cur, onClose }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!payoutId) return;
    setD(null); setErr("");
    api.get(`/dashboard/payouts/${payoutId}/reconcile`).then(({ data }) => setD(data)).catch((e) => setErr(e?.response?.data?.detail || "Couldn't load this payout"));
  }, [payoutId]);
  const c = d?.currency || cur;
  return (
    <Dialog open={!!payoutId} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="payout-reconcile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Landmark className="h-5 w-5 text-primary" />Payout reconciliation</DialogTitle>
          <DialogDescription>{d ? `Arrives ${d.payout.arrival_date} · ${fmtCurrency(d.payout.amount, c, 2)} · ${d.payout.status.replace("_", " ")}` : "Matching Stripe to your sales…"}</DialogDescription>
        </DialogHeader>
        {err && <p className="text-sm text-destructive" data-testid="payout-reconcile-error">{err}</p>}
        {!d && !err && <div className="h-32 animate-pulse rounded-xl bg-muted" />}
        {d && (
          <div className="space-y-4">
            <div className={`rounded-xl border p-3 flex items-center gap-2 text-sm ${d.reconciled ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="payout-reconcile-status">
              {d.reconciled ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
              {d.reconciled ? `Reconciled — all ${d.total} transactions match an Azordi sale.` : `${d.matched} of ${d.total} transactions matched. Unmatched lines are below.`}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat l="Card sales" v={fmtCurrency(d.gross, c, 2)} />
              <Stat l="Refunds" v={d.refunds ? `−${fmtCurrency(d.refunds, c, 2)}` : "—"} />
              <Stat l="Stripe fees" v={`−${fmtCurrency(d.fees, c, 2)}`} tone="text-muted-foreground" />
              <Stat l="Paid to bank" v={fmtCurrency(d.payout.amount, c, 2)} tone="text-primary" />
            </div>
            <div className="rounded-xl border divide-y text-sm" data-testid="payout-lines">
              {d.lines.map((l) => (
                <div key={l.id} className="px-3 py-2 flex items-start gap-3" data-testid={`payout-line-${l.id}`}>
                  <div className="w-[76px] shrink-0 text-xs text-muted-foreground tabular-nums">{l.created}</div>
                  <div className="flex-1 min-w-0">
                    {l.matched ? <><div className="font-medium truncate">{l.matched.client_name || "Client"} · {l.matched.service_name}</div><div className="text-xs text-muted-foreground">{l.matched.invoice_no ? `${l.matched.invoice_no} · ` : ""}{l.matched.date}{l.matched.kind ? ` · ${l.matched.kind.replace("_", " ")}` : ""}</div></>
                      : <><div className="font-medium text-amber-800">Not matched to a sale</div><div className="text-xs text-muted-foreground truncate">{l.description || l.type}{l.payment_intent ? ` · ${l.payment_intent}` : ""}</div></>}
                  </div>
                  <div className="text-right shrink-0"><div className={`tabular-nums font-semibold ${l.amount < 0 ? "text-destructive" : ""}`}>{fmtCurrency(l.amount, c, 2)}</div><div className="text-[11px] text-muted-foreground tabular-nums">fee {fmtCurrency(l.fee, c, 2)}</div></div>
                </div>
              ))}
              {d.lines.length === 0 && <div className="px-3 py-4 text-sm text-muted-foreground">No transactions in this payout.</div>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
